<!--
 * @Author: qoder
 * @Description: 创建/复制维修工单
-->
<template>
  <ykc-form :model="ruleForm" :rules="rules" ref="ruleForm" class="form-container">
    <ykc-form-item label="所属平台" prop="platform">
      <ykc-dropdown
        placeholder="请选择所属平台"
        :data="platformData"
        v-model="ruleForm.platform"></ykc-dropdown>
    </ykc-form-item>
    <ykc-form-item label="电站" prop="stationId">
      <ykc-dropdown
        placeholder="请选择电站"
        :data="stationOptions"
        :configSet="{ label: 'stationName', value: 'stationId' }"
        :remote="true"
        :loading="stationLoading"
        v-model="ruleForm.stationId"
        @change="handleStationChange"></ykc-dropdown>
    </ykc-form-item>
    <ykc-form-item label="充电桩编号" prop="gunId">
      <ykc-dropdown
        placeholder="请选择充电桩编号"
        :data="deviceData"
        :configSet="{ label: 'gunCode', value: 'gunId' }"
        :remote="true"
        :loading="deviceLoading"
        v-model="ruleForm.gunId"
        @change="handleDeviceChange"></ykc-dropdown>
    </ykc-form-item>
    <ykc-form-item label="工单类型" prop="orderType">
      <ykc-dropdown
        placeholder="请选择工单类型"
        :data="orderTypeData"
        v-model="ruleForm.orderType"></ykc-dropdown>
    </ykc-form-item>
    <ykc-form-item label="故障分类" prop="faultCategory">
      <ykc-dropdown
        placeholder="请选择故障分类"
        :data="faultCategoryData"
        v-model="ruleForm.faultCategory"></ykc-dropdown>
    </ykc-form-item>
    <ykc-form-item label="故障等级">
      <ykc-dropdown
        placeholder="请选择故障等级（选填）"
        :data="faultLevelData"
        v-model="ruleForm.faultLevel"></ykc-dropdown>
    </ykc-form-item>
    <ykc-form-item label="故障事项">
      <ykc-input placeholder="请输入故障事项（选填）" v-model="ruleForm.faultDes"></ykc-input>
    </ykc-form-item>
    <ykc-form-item label="故障描述" prop="faultDescription">
      <ykc-input
        type="textarea"
        :rows="4"
        maxlength="500"
        placeholder="请输入故障描述"
        v-model="ruleForm.faultDescription"></ykc-input>
    </ykc-form-item>
    <ykc-form-item label="现场照片" prop="images">
      <ykc-upload
        :fileList="fileList"
        :limit="5"
        accept=".jpg,.jpeg,.png"
        @handleChange="handleUpload"
        @handleRemove="handleRemove"></ykc-upload>
    </ykc-form-item>
  </ykc-form>
</template>

<script>
  import { repairOrderApi, uploadUtils } from '@/service/apis';

  export default {
    name: 'CreateOrCopy',
    props: {
      drawerType: {
        type: String,
        default: 'create',
      },
      copyData: {
        type: Object,
        default: null,
      },
    },
    data() {
      return {
        stationName: '',
        isInitializing: false, // 复制初始化时跳过 platform/stationId watcher 的清空逻辑
        stationLoading: false,
        stationOptions: [],
        deviceData: [],
        deviceLoading: false,
        fileList: [],
        selectedDevice: null,
        ruleForm: {
          platform: 1,
          stationId: '',
          gunId: '',
          gunCode: '',
          gunName: '',
          orderType: '',
          faultCategory: '',
          faultLevel: '',
          faultDes: '',
          faultDescription: '',
          images: [],
        },
        rules: {
          platform: [{ required: true, message: '请选择所属平台', trigger: 'change' }],
          stationId: [{ required: true, message: '请选择电站', trigger: 'change' }],
          gunId: [{ required: true, message: '请选择充电桩编号', trigger: 'change' }],
          orderType: [{ required: true, message: '请选择工单类型', trigger: 'change' }],
          faultCategory: [{ required: true, message: '请选择故障分类', trigger: 'change' }],
          faultDescription: [{ required: true, message: '请输入故障描述', trigger: 'blur' }],
          images: [
            {
              required: true,
              validator: (rule, value, callback) => {
                if (this.ruleForm.images.length === 0) {
                  callback(new Error('请上传现场照片'));
                } else {
                  callback();
                }
              },
              trigger: 'change',
            },
          ],
        },
        selectedStationInfo: null,
        faultCategoryData: [
          { id: '离线', name: '离线' },
          { id: '硬件故障', name: '硬件故障' },
        ],
        platformData: [
          { id: 1, name: '淘淘充' },
          { id: 2, name: '益虫' },
        ],
        faultLevelData: [
          { id: 1, name: 'I级（致命故障）' },
          { id: 2, name: 'II级（中度故障）' },
          { id: 3, name: 'III级（一般故障）' },
          { id: 4, name: 'IV级（轻微告警）' },
        ],
      };
    },
    computed: {
      orderTypeData() {
        const base = [{ id: 'repair', name: '电桩维修' }];
        // 私桩（isPrivatePile=1）才显示电桩更换选项
        if (this.selectedStationInfo && Number(this.selectedStationInfo.isPrivatePile) === 1) {
          base.push({ id: 'replace', name: '电桩更换' });
        }
        return base;
      },
    },
    watch: {
      copyData: {
        immediate: true,
        deep: true,
        handler(val) {
          if (val && this.drawerType === 'copy') {
            // 标记正在初始化，防止 platform watcher 清空复制的数据（益虫 platform 从1变2会触发）
            this.isInitializing = true;
            this.ruleForm = {
              platform: val.platform || 1,
              stationId: val.stationId,
              gunId: val.deviceId,
              gunCode: val.gunCode || val.deviceNo,
              gunName: val.gunName || '',
              // 后端 orderType 为 Integer（1=电桩维修，2=电桩更换），前端下拉用字符串 ID
              orderType: val.orderType === 1 ? 'repair' : val.orderType === 2 ? 'replace' : '',
              faultCategory: val.faultCategory || '',
              faultLevel: val.faultLevel || '',
              faultDes: val.faultItem || '',
              faultDescription: val.faultDesc || '',
              images: val.faultImages || [],
            };
            this.stationName = val.stationName || '';
            // 初始化电站下拉选项，以便 el-select 显示已选电站名称
            if (val.stationId && val.stationName) {
              this.stationOptions = [
                {
                  stationId: val.stationId,
                  stationName: val.stationName,
                  isPrivatePile: val.isPrivatePile || (val.stationType === 2 ? 1 : 0),
                },
              ];
              this.selectedStationInfo = this.stationOptions[0];
            }
            if (val.stationId) {
              this.loadDeviceList(val.stationId);
            }
            // 复制时初始化已上传图片的 fileList
            if (val.faultImages && val.faultImages.length > 0) {
              this.fileList = val.faultImages.map((url, index) => ({
                name: `image_${index}`,
                url,
                uid: Date.now() + index,
              }));
            }
            // 等待所有 watcher 执行完毕后解除标记
            this.$nextTick(() => {
              this.isInitializing = false;
            });
          }
        },
      },
      'ruleForm.platform': {
        immediate: true,
        handler(val) {
          if (this.isInitializing) return;
          // 切换平台时清空已选电站和设备
          this.ruleForm.stationId = '';
          this.ruleForm.gunId = '';
          this.ruleForm.gunCode = '';
          this.ruleForm.gunName = '';
          this.stationOptions = [];
          this.deviceData = [];
          this.selectedStationInfo = null;
          this.selectedDevice = null;
          if (val) {
            this.loadStationList();
          }
        },
      },
      'ruleForm.stationId': {
        handler(val) {
          if (this.isInitializing) return;
          if (val) {
            this.loadDeviceList(val);
          } else {
            this.deviceData = [];
          }
        },
      },
    },
    methods: {
      async loadStationList() {
        if (!this.ruleForm.platform) return;
        try {
          this.stationLoading = true;
          const api =
            this.ruleForm.platform === 2
              ? repairOrderApi.getStationListYc
              : repairOrderApi.getStationList;
          const res = await api({
            pageNum: 1,
            pageSize: 200,
          });
          this.stationOptions = res?.records || [];
        } catch (error) {
          console.error('加载电站列表失败', error);
          this.stationOptions = [];
        } finally {
          this.stationLoading = false;
        }
      },
      async loadDeviceList(stationId) {
        if (!stationId) {
          this.deviceData = [];
          return;
        }
        try {
          this.deviceLoading = true;
          const api =
            this.ruleForm.platform === 2
              ? repairOrderApi.getDeviceListYc
              : repairOrderApi.getDeviceList;
          const res = await api({ stationId });
          this.deviceData = res || [];
        } catch (error) {
          console.error('加载设备列表失败', error);
          this.deviceData = [];
        } finally {
          this.deviceLoading = false;
        }
      },
      // 电站选择变化
      handleStationChange(stationId) {
        const selected = this.stationOptions.find(item => item.stationId === stationId);
        if (selected) {
          this.stationName = selected.stationName;
          this.selectedStationInfo = selected;
        } else {
          this.selectedStationInfo = null;
        }
        this.ruleForm.gunId = '';
        this.ruleForm.gunCode = '';
        this.ruleForm.gunName = '';
        // 公桩不显示电桩更换，如果当前选了更换则重置
        if (
          this.ruleForm.orderType === 'replace' &&
          !(this.selectedStationInfo && Number(this.selectedStationInfo.isPrivatePile) === 1)
        ) {
          this.ruleForm.orderType = '';
        }
        this.$nextTick(() => {
          this.$refs.ruleForm && this.$refs.ruleForm.clearValidate(['stationId', 'gunId']);
        });
      },
      handleDeviceChange(gunId) {
        this.ruleForm.gunId = gunId;
        const device = this.deviceData.find(item => item.gunId === gunId);
        if (device) {
          this.ruleForm.gunCode = device.gunCode || device.deviceNo;
          this.ruleForm.gunName = device.gunName || '';
          this.selectedDevice = device;
          // 根据设备状态自动设置故障分类
          if (device.status === 0) {
            this.ruleForm.faultCategory = '离线';
          } else if (device.status === 1) {
            this.ruleForm.faultCategory = '硬件故障';
          }
        }
        // el-select在ykc-form-item slot中，el.form.change事件无法冒泡到el-form-item
        // 手动清除校验状态
        this.$nextTick(() => {
          this.$refs.ruleForm && this.$refs.ruleForm.clearValidate(['gunId']);
        });
      },
      // 上传图片：先上传到OSS获取真实URL，再保存到表单
      handleUpload(file, fileList, isCheck) {
        if (!isCheck) return;
        const formData = new FormData();
        formData.append('file', file.raw);
        formData.append('bizName', 'poly/repair');
        uploadUtils
          .uploadImg(formData)
          .then(res => {
            // res 为后端返回的 OSS 图片地址
            const ossUrl = res;
            // 更新 fileList 中对应文件的 url 为真实 OSS 地址
            const target = this.fileList.find(f => f.uid === file.uid);
            if (target) {
              target.url = ossUrl;
              target.ossUrl = ossUrl;
            } else {
              this.fileList.push({
                ...file,
                url: ossUrl,
                ossUrl,
              });
            }
            // 同步到 ruleForm.images
            this.ruleForm.images = this.fileList.map(f => f.ossUrl || f.url);
          })
          .catch(err => {
            console.error('上传图片失败', err);
            this.$message.error('上传图片失败，请重试');
            // 上传失败则移除该文件
            this.fileList = this.fileList.filter(f => f.uid !== file.uid);
            this.ruleForm.images = this.fileList.map(f => f.ossUrl || f.url);
          });
      },
      handleRemove(file) {
        this.fileList = this.fileList.filter(f => f.uid !== file.uid);
        this.ruleForm.images = this.fileList.map(f => f.ossUrl || f.url);
      },
      async submit() {
        try {
          // ykc-form.validate是回调模式，需包装为Promise
          await new Promise((resolve, reject) => {
            this.$refs.ruleForm.validate(valid => {
              if (valid) {
                resolve(true);
              } else {
                reject(new Error('表单校验失败'));
              }
            });
          });
          const params = {
            ...this.ruleForm,
            stationName: this.stationName,
            creatorId: localStorage.userId,
            creatorName: localStorage.accountName,
          };
          if (this.drawerType === 'copy' && this.copyData?.id) {
            params.copyFromId = this.copyData.id;
          }
          await repairOrderApi.submit(params);
          this.$message.success(this.drawerType === 'copy' ? '复制成功' : '创建成功');
          this.$emit('success');
          return true;
        } catch (error) {
          console.error('提交失败', error);
          return false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .form-container {
    padding: 20px;
  }
</style>
